import {uniq} from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {buildUniqueOnlineTeammateIdentifier} from '../../../models/push/onlineTeammateHelpers';
import {AppState} from '../../appState';

export function getOnlineTeammates(state: AppState) {
  return state.data.onlineTeammates;
}

export const getUniqueOnlineTeammateIdentifiersSet = createSelector(
  getOnlineTeammates,
  (onlineTeammates): ReadonlySet<string> | undefined => {
    if (!onlineTeammates) {
      return undefined;
    }

    const uniqueOnlineTeammateIdentifiers = onlineTeammates.map(buildUniqueOnlineTeammateIdentifier);
    return new Set(uniqueOnlineTeammateIdentifiers);
  },
);

export const getOnlineTeammateIds = createSelector(
  getOnlineTeammates,
  (onlineTeammates): ReadonlyArray<number> | undefined =>
    onlineTeammates ? uniq(onlineTeammates.map((t) => t.teammateId)) : undefined,
);

export const getOnlineTeammateIdsSet = createSelector(
  getOnlineTeammateIds,
  (onlineTeammateIds): ReadonlySet<number> | undefined =>
    onlineTeammateIds ? new Set(onlineTeammateIds) : undefined,
);

export enum TeammateOnlineStatusesEnum {
  UNKNOWN = 'UNKNOWN',
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

export type FindOnlineStatus = (teammate: TeammateModel) => TeammateOnlineStatusesEnum;

export const getFindOnlineStatus = createSelector(
  getOnlineTeammateIdsSet,
  (onlineTeammateIds) => (teammate: TeammateModel) => findOnlineStatus(onlineTeammateIds, teammate.id),
);

export function makeGetOnlineStatus<P>(getTeammateId: AppParametricSelector<P, number>) {
  return createSelector([getOnlineTeammateIdsSet, getTeammateId], (onlineTeammateIds, teammateId) =>
    findOnlineStatus(onlineTeammateIds, teammateId),
  );
}

function findOnlineStatus(
  teammateIds: ReadonlySet<number> | undefined,
  teammateId: number,
): TeammateOnlineStatusesEnum {
  if (!teammateIds) {
    return TeammateOnlineStatusesEnum.UNKNOWN;
  }

  if (teammateIds.has(teammateId)) {
    return TeammateOnlineStatusesEnum.ONLINE;
  }

  return TeammateOnlineStatusesEnum.OFFLINE;
}
